export function normalizeFreeEntitlements(freeEntitlements) {
  const source = freeEntitlements || {};
  return {
    zodiac: {
      remainingCount: source?.zodiac?.remainingCount ?? source?.zodiac?.remaining_count ?? 0,
      usedCount: source?.zodiac?.usedCount ?? source?.zodiac?.used_count ?? 0,
      limitCount: source?.zodiac?.limitCount ?? source?.zodiac?.limit_count ?? 0,
    },
    tarot: {
      remainingCount: source?.tarot?.remainingCount ?? source?.tarot?.remaining_count ?? 0,
      usedCount: source?.tarot?.usedCount ?? source?.tarot?.used_count ?? 0,
      limitCount: source?.tarot?.limitCount ?? source?.tarot?.limit_count ?? 0,
    },
    wedding: {
      remainingCount: source?.wedding?.remainingCount ?? source?.wedding?.remaining_count ?? 0,
      usedCount: source?.wedding?.usedCount ?? source?.wedding?.used_count ?? 0,
      limitCount: source?.wedding?.limitCount ?? source?.wedding?.limit_count ?? 0,
    },
  };
}

export function normalizeAuthUser(user) {
  if (!user) return null;

  const freeEntitlements = normalizeFreeEntitlements(user.free_entitlements || user.freeEntitlements);

  return {
    id: user.id,
    email: user.email || "",
    fullName: user.fullName || user.fullname || user.full_name || "",
    emailVerified: user.emailVerified ?? user.email_verified ?? false,
    emailVerifiedAt: user.emailVerifiedAt || user.email_verified_at || "",
    trustedGoogleAccount: user.trustedGoogleAccount ?? user.trusted_google_account ?? false,
    creditsBalance: user.creditsBalance ?? user.credits_balance ?? 0,
    activeSubscription: user.activeSubscription ?? user.active_subscription ?? false,
    subscriptionRenewalCancelledAt:
      user.subscriptionRenewalCancelledAt ?? user.subscription_renewal_cancelled_at ?? "",
    predictionCosts: user.predictionCosts || user.prediction_costs || {},
    freeEntitlements,
    birthDate: user.birthDate || user.birth_date || "",
    birthTime: user.birthTime || user.birth_time || "",
    birthPlace: user.birthPlace || user.birthplace || "",
  };
}

export function applyAuthUserUpdate(targetUser, nextUser) {
  if (!targetUser || !nextUser) return targetUser;

  const normalized = normalizeAuthUser({ ...targetUser, ...nextUser });
  Object.assign(targetUser, normalized);
  return targetUser;
}

/**
 * After JSON login succeeds, sync the in-memory auth snapshot so SPA navigation to
 * protected pages is not rejected by resolveRedirectForPage (boot payload is still signed-out).
 * Mutates the existing cache object in place so AppShell props stay live without a remount.
 */
export function applyAuthSession(payload) {
  if (typeof window === "undefined") return getAuthContext();

  const rawUser = payload?.user;
  if (!rawUser || rawUser.id == null) return getAuthContext();

  const bootPayload = { signedIn: true, user: rawUser };
  window.AUTH_CONTEXT = bootPayload;

  const el = document.getElementById(AUTH_BOOTSTRAP_ID);
  if (el) {
    try {
      el.textContent = JSON.stringify(bootPayload);
    } catch (_) {
      /* ignore */
    }
  }

  const normalizedUser = normalizeAuthUser(rawUser);
  const sig = authBootSignature(bootPayload);

  if (!authViewCache) {
    authViewCache = { signedIn: true, user: normalizedUser };
  } else {
    authViewCache.signedIn = true;
    if (authViewCache.user && normalizedUser) {
      Object.assign(authViewCache.user, normalizedUser);
    } else {
      authViewCache.user = normalizedUser;
    }
  }
  authViewCacheSig = sig;

  return authViewCache;
}

const AUTH_BOOTSTRAP_ID = "ecliptica-auth-bootstrap";

let authViewCache = null;
let authViewCacheSig = null;

/** Treat as signed in when explicit flag is true or server embedded a user id (Rails may use snake_case keys). */
export function inferSignedIn(raw) {
  if (!raw || typeof raw !== "object") return false;
  const explicit = raw.signedIn ?? raw.signed_in;
  if (explicit === false || explicit === "false") return false;
  if (explicit === true || explicit === "true") return true;
  const u = raw.user;
  if (u && typeof u === "object") {
    const id = u.id ?? u.user_id;
    if (id != null && id !== "") return true;
  }
  return false;
}

function readRawAuthPayload() {
  if (typeof document !== "undefined") {
    const el = document.getElementById(AUTH_BOOTSTRAP_ID);
    const text = el?.textContent?.trim();
    if (text) {
      try {
        return JSON.parse(text);
      } catch (_) {
        /* fall through */
      }
    }
  }
  if (typeof window !== "undefined" && window.AUTH_CONTEXT && typeof window.AUTH_CONTEXT === "object") {
    return window.AUTH_CONTEXT;
  }
  return { signedIn: false, user: null };
}

function authBootSignature(raw) {
  if (!raw || typeof raw !== "object") return "guest";
  const signedIn = inferSignedIn(raw);
  const u = raw.user;
  if (!signedIn || u == null) return signedIn ? "signed_in_no_user" : "guest";
  if (typeof u !== "object") return "in:invalid_user";
  const id = u.id ?? u.user_id ?? "";
  const email = String(u.email ?? "");
  return `in:${id}:${email}`;
}

/**
 * Reads the server-rendered boot payload when accessed (not at module load).
 * Reuses one object per boot signature so UI code can mutate `user` in place (credits, profile patches).
 */
export function getAuthContext() {
  if (typeof window === "undefined") {
    return { signedIn: false, user: null };
  }
  const raw = readRawAuthPayload();
  const signedIn = inferSignedIn(raw);
  const sig = authBootSignature(raw);
  if (!authViewCache || authViewCacheSig !== sig) {
    authViewCache = {
      signedIn,
      user: normalizeAuthUser(raw.user),
    };
    authViewCacheSig = sig;
  } else {
    authViewCache.signedIn = signedIn;
  }
  return authViewCache;
}

const I18N_BOOTSTRAP_ID = "ecliptica-i18n-bootstrap";

function i18nPayloadLooksLoaded(obj) {
  return (
    obj &&
    typeof obj === "object" &&
    obj.en &&
    typeof obj.en === "object" &&
    Object.keys(obj.en).length > 0
  );
}

/**
 * Resolves UI string tables after the inline boot script (or JSON bootstrap tag) runs.
 * Avoid reading window.I18N_STRINGS only at module load — the esbuild bundle can execute
 * before the ERB inline script assigns it, which used to leave an empty snapshot.
 */
export function getI18nStrings() {
  if (typeof window === "undefined") return { en: {}, vi: {} };

  if (i18nPayloadLooksLoaded(window.I18N_STRINGS)) {
    return window.I18N_STRINGS;
  }

  if (typeof document !== "undefined") {
    const el = document.getElementById(I18N_BOOTSTRAP_ID);
    const text = el?.textContent?.trim();
    if (text) {
      try {
        const parsed = JSON.parse(text);
        if (i18nPayloadLooksLoaded(parsed)) {
          window.I18N_STRINGS = parsed;
          return parsed;
        }
      } catch (_) {
        /* ignore */
      }
    }
  }

  return window.I18N_STRINGS && typeof window.I18N_STRINGS === "object"
    ? window.I18N_STRINGS
    : { en: {}, vi: {} };
}

export const runtimeConfig = {
  get accentPreset() {
    if (typeof window === "undefined") return "teal-blue";
    return window.ECLIPTICA_ACCENT_PRESET || "teal-blue";
  },
  get i18nStrings() {
    return getI18nStrings();
  },
  get initialPage() {
    return typeof window !== "undefined" ? window.INITIAL_PAGE : undefined;
  },
  get initialAuthMode() {
    return typeof window !== "undefined" ? window.INITIAL_AUTH_MODE : undefined;
  },
  get authContext() {
    return getAuthContext();
  },
};

